import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, Validators } from '@angular/forms';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { EfficiencyAnalysisApiService } from '@app/shared/services/api/efficiency-analysis-api.service';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { BuildingTypesService } from '@app/shared/services/api/building-types.services';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { isNil, orderBy } from 'lodash';
import { cssVariables } from '@env/css-vars';
import { BuildingEfficiencyWizardStore } from '@app/efficiency-analysis/components/building-efficiency-wizard/building-efficiency-wizard.component.store';
import { EfficiencyAnalysisDatastoreService } from '@app/efficiency-analysis/services/efficiency-analysis-datastore.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { Observable, Subject, forkJoin, of, throwError } from 'rxjs';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import moment from 'moment';
import { AuthService } from '@app/shared/services/auth.service';
import { catchError, first, mergeAll, mergeMap, switchMap, takeUntil, toArray } from 'rxjs/operators';
import { ComparisonValues } from '@app/efficiency-analysis/models/comparison-values';
import { NewComparisonValues } from '@app/efficiency-analysis/models/new-comparison-values';
import { HttpErrorResponse } from '@angular/common/http';
import { Buildings } from '@app/energy-guard/models/buildings';
import { EfficiencyAnalysisDto } from '@app/shared/models/efficiency-analysis/efficiency-analysis-dto';
import { BuildingTypes } from '@app/shared/models/building-types';
import { IBuildingEfficiencyData } from '@app/efficiency-analysis/models/buildings/building-efficiency-data';

@Component({
  selector: 'app-modify-reference-values',
  templateUrl: './modify-reference-values.component.html',
  styleUrls: ['./modify-reference-values.component.scss'],
})
export class ModifyReferenceValuesComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  private buildingTypes: BuildingTypes[] = [];
  private newComparisonValues: NewComparisonValues = {} as NewComparisonValues;
  private selectedBuildingType: ComparisonValues = {} as ComparisonValues;
  public buildingTypeName?: string = '';
  public comparisonValues: ComparisonValues[] = [];
  public buildingType = new FormControl('', [Validators.required]);
  public refValElectricity = new FormControl('', [Validators.required, this.isBetween1and1000]);
  public refValHeat = new FormControl('', [Validators.required, this.isBetween1and1000]);
  public averageElectricity = new FormControl('', [Validators.required, this.isBetween1and1000]);
  public averageHeat = new FormControl('', [Validators.required, this.isBetween1and1000]);
  public maxScaleElectricity = new FormControl('', [Validators.required, this.isBetween1and1000]);
  public maxScaleHeat = new FormControl('', [Validators.required, this.isBetween1and1000]);

  @Output() closeEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public overlayLoaderService: OverlayLoaderService,
    public matDialog: MatDialog,
    public efficiencyAnalysisService: EfficiencyAnalysisService,
    public translate: TranslateService,
    public authService: AuthService,
    private efficiencyAnalysisApiService: EfficiencyAnalysisApiService,
    private buildingTypesService: BuildingTypesService,
    private _buildingEfficiencyWizardStore: BuildingEfficiencyWizardStore,
    private _efficiencyAnalysisDataStore: EfficiencyAnalysisDatastoreService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private localNumberPipe: LocalNumberPipe
  ) {}

  public ngOnInit(): void {
    this.initializeValues();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public setFormValues(buildingType: ComparisonValues): void {
    this.selectedBuildingType = buildingType;
    this.buildingTypeName = buildingType.buildingTypeName;
    this.buildingType.setValue(buildingType.buildingTypeName || null);
    this.refValElectricity.setValue(this.localize(buildingType.referenceValueElectricity));
    this.refValHeat.setValue(this.localize(buildingType.referenceValueHeat));
    this.averageElectricity.setValue(this.localize(buildingType.characteristicValueElectricity));
    this.averageHeat.setValue(this.localize(buildingType.characteristicValueHeat));
    this.maxScaleElectricity.setValue(this.localize(buildingType.colorElectricityMax));
    this.maxScaleHeat.setValue(this.localize(buildingType.colorHeatMax));
  }

  public checkButtonStatus(): boolean {
    const result = !(
      this.buildingType.valid &&
      this.refValElectricity.errors?.isValid &&
      this.refValHeat.errors?.isValid &&
      this.averageElectricity.errors?.isValid &&
      this.averageHeat.errors?.isValid &&
      this.maxScaleElectricity.errors?.isValid &&
      this.maxScaleHeat.errors?.isValid
    );
    return result;
  }

  public numberOnly(event: KeyboardEvent): boolean {
    const allowInput = /^\d$/.test(event.key);
    return allowInput;
  }

  public openSaveConfirmationDialog(): void {
    this.matDialog
      .open(NotificationDialogComponent, {
        data: {
          descriptionText: this.translate.instant('module.efficiencyAnalysis.efficiencyAnalysisConfigDialog.saveConfirmationText'),
          confirmText: 'Ja',
          declineText: 'Nein',
        },
        panelClass: ['-md'],
      })
      .afterClosed()
      .subscribe((confirm: boolean) => {
        if (confirm) {
          this.overlayLoaderService.isLoading = true;
          this.save();
        }
      });
  }

  public cancel(): void {
    this.closeEvent.emit(true);
  }

  public focus(value: string): void {
    switch (value) {
      case 'refValElectricity':
        this.refValElectricity.setValue(this.unlocalize(this.refValElectricity.value).toString());
        break;
      case 'refValHeat':
        this.refValHeat.setValue(this.unlocalize(this.refValHeat.value).toString());
        break;
      case 'averageElectricity':
        this.averageElectricity.setValue(this.unlocalize(this.averageElectricity.value).toString());
        break;
      case 'averageHeat':
        this.averageHeat.setValue(this.unlocalize(this.averageHeat.value).toString());
        break;
      case 'maxScaleElectricity':
        this.maxScaleElectricity.setValue(this.unlocalize(this.maxScaleElectricity.value).toString());
        break;
      case 'maxScaleHeat':
        this.maxScaleHeat.setValue(this.unlocalize(this.maxScaleHeat.value).toString());
        break;
    }
  }

  public blur(value: string): void {
    switch (value) {
      case 'refValElectricity':
        this.refValElectricity.setValue(this.localize(this.refValElectricity.value));
        break;
      case 'refValHeat':
        this.refValHeat.setValue(this.localize(this.refValHeat.value));
        break;
      case 'averageElectricity':
        this.averageElectricity.setValue(this.localize(this.averageElectricity.value));
        break;
      case 'averageHeat':
        this.averageHeat.setValue(this.localize(this.averageHeat.value));
        break;
      case 'maxScaleElectricity':
        this.maxScaleElectricity.setValue(this.localize(this.maxScaleElectricity.value));
        break;
      case 'maxScaleHeat':
        this.maxScaleHeat.setValue(this.localize(this.maxScaleHeat.value));
        break;
    }
  }

  private initializeValues(): void {
    this.overlayLoaderService.isLoading = true;

    forkJoin([this.efficiencyAnalysisApiService.getComparisonValues(), this.buildingTypesService.getAll()])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([comparisonValues, buildingTypes]: [ComparisonValues[], BuildingTypes[]]) => {
        this.buildingTypes = buildingTypes;
        comparisonValues.forEach((val: ComparisonValues) => this.mapComparisonValues(val));
        this.comparisonValues = orderBy(this.comparisonValues, (values: ComparisonValues) => values.buildingTypeName);
        this.buildingTypeName = this.comparisonValues[0].buildingTypeName;
        this.overlayLoaderService.isLoading = false;
      });
  }

  private mapComparisonValues(value: ComparisonValues): void {
    const isFound = this.buildingTypes.find((building: BuildingTypes) => building.guid === value.buildingTypeId);
    if (isFound) {
      value.buildingTypeName = isFound.name;
      this.comparisonValues.push(value);
    }
  }

  private isBetween1and1000(control: FormControl): { isValid: boolean } {
    const value = parseInt(control.value.toString().split('.').join(''), 10);
    const result = value >= 1 && value <= 1000 ? { isValid: true } : { isValid: false };
    return result;
  }

  private save(): void {
    this.overlayLoaderService.isLoading = true;
    this.newComparisonValues = {
      guid: this.selectedBuildingType.guid,
      referenceValueElectricity: this.unlocalize(this.refValElectricity.value),
      referenceValueHeat: this.unlocalize(this.refValHeat.value),
      characteristicValueElectricity: this.unlocalize(this.averageElectricity.value),
      characteristicValueHeat: this.unlocalize(this.averageHeat.value),
      colorElectricityMax: this.unlocalize(this.maxScaleElectricity.value),
      colorHeatMax: this.unlocalize(this.maxScaleHeat.value),
    };

    this.efficiencyAnalysisApiService
      .updateComparisonValues(this.newComparisonValues)
      .pipe(
        toArray(),
        catchError(this.handleError),
        switchMap(() => this.recalculateEfficiency()),
        first()
      )
      .subscribe(() => {
        this.overlayLoaderService.isLoading = false;
        this.displaySavingConfirmationDialog();
      });
  }

  private recalculateEfficiency(): Observable<[IBuildingEfficiencyData[], ComparisonValues[], BuildingTypes[]]> {
    const currentMunicipalityKey = this.userMunicipalitiesService.currentMunicipality.key;

    return this.efficiencyAnalysisApiService.getBuildingsByBuildingTypeId(currentMunicipalityKey, this.selectedBuildingType.buildingTypeId).pipe(
      switchMap((buildings: Buildings[]) => {
        if (buildings.length === 0) {
          return of([]);
        }

        const buildingGuidArr = Array.from(new Set(buildings.map((item: Buildings) => item.guid)));
        return of(buildingGuidArr);
      }),
      mergeAll(),
      mergeMap((buildingGuid: string) => this.efficiencyAnalysisApiService.getByBuildingId(buildingGuid, currentMunicipalityKey)),
      mergeMap((analysis: EfficiencyAnalysisDto[]) => {
        let buildingData: EfficiencyAnalysisDto = analysis[0];
        if (analysis.length > 0) {
          buildingData =
            analysis.length > 1 ? analysis.reduce((a: EfficiencyAnalysisDto, b: EfficiencyAnalysisDto) => (a.modifyDate > b.modifyDate ? a : b)) : buildingData;
          this.efficiencyAnalysisService.isRecalculated = true;
          const analysisData = this.mapSaveData(buildingData, buildingData.guid);
          return this._buildingEfficiencyWizardStore.saveEfficiencyAnalysis(analysisData, currentMunicipalityKey);
        }
        return of(null);
      }),
      toArray(),
      switchMap(() => this._efficiencyAnalysisDataStore.loadBuildingEfficiencyAnalysis())
    );
  }

  private mapSaveData(data: EfficiencyAnalysisDto, guid: string | null = null): Partial<EfficiencyAnalysisDto> {
    const efficiencyPercentageElectricity = this.efficiencyAnalysisService.getEfficiencyPercentage(
      data.electricityComputedValue,
      data.electricityMinDataRange,
      this.newComparisonValues.colorElectricityMax
    );

    const efficiencyPercentageHeat = this.efficiencyAnalysisService.getEfficiencyPercentage(
      data.heatComputedValue,
      data.heatMinDataRange,
      this.newComparisonValues.colorHeatMax
    );

    const saveData: Partial<EfficiencyAnalysisDto> = {
      buildingId: data.buildingId,
      floorArea: data.floorArea,
      electricityConsumption: data.electricityConsumption,
      averageElectricityConsumption: this.newComparisonValues.characteristicValueElectricity,
      electricityGuideValue: this.newComparisonValues.referenceValueElectricity,
      electricityMinDataRange: data.electricityMinDataRange,
      electricityMaxDataRange: this.newComparisonValues.colorElectricityMax,
      heatingOilConsumption: data.heatingOilConsumption,
      naturalGasConsumption: data.naturalGasConsumption,
      lpgConsumption: data.lpgConsumption,
      districtHeatingConsumption: data.districtHeatingConsumption,
      electricityHeatConsumption: data.electricityHeatConsumption,
      pelletWoodConsumption: data.pelletWoodConsumption,
      averageHeatConsumption: this.newComparisonValues.characteristicValueHeat,
      heatGuideValue: this.newComparisonValues.referenceValueHeat,
      heatMinDataRange: data.heatMinDataRange,
      heatMaxDataRange: this.newComparisonValues.colorHeatMax,
      electricityColorStatus: this.efficiencyAnalysisService.getEAColorStatusTriColor(efficiencyPercentageElectricity),
      heatColorStatus: this.efficiencyAnalysisService.getEAColorStatusTriColor(efficiencyPercentageHeat),
      electricityComputedValue: data.electricityComputedValue,
      heatComputedValue: data.heatComputedValue,
      efficiencyAnalysisDate: data.efficiencyAnalysisDate,
      efficiencyPercentageElectricity: efficiencyPercentageElectricity,
      efficiencyPercentageHeat: efficiencyPercentageHeat,
      efficiencyAnalysisStartDate: data.efficiencyAnalysisStartDate,
      efficiencyAnalysisEndDate: data.efficiencyAnalysisEndDate,
      isElectricityValueSuggested: data.isElectricityValueSuggested,
      isHeatValueSuggested: data.isHeatValueSuggested,
      notes: data.notes,
      modifyDate: moment.utc().toDate(),
      modifyUser: this.authService.currentUser.guid,
    };

    if (!isNil(guid)) {
      saveData.guid = guid;
    } else {
      saveData.creationDate = moment.utc().toDate();
    }

    return saveData;
  }

  private displaySavingConfirmationDialog(): void {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: this.translate.instant('module.efficiencyAnalysis.efficiencyAnalysisConfigDialog.successValuesChangeText'),
        orientation: 'row',
        alignment: 'start',
      },
      panelClass: ['-md'],
    });
    this.reset();
    this.initializeValues();
  }

  private reset(): void {
    this.comparisonValues = [];
    this.buildingTypes = [];
    this.buildingTypeName = '';
  }

  private localize(value: any): string {
    return value ? this.localNumberPipe.transform(parseInt(value, 10), 0, 0) : '';
  }

  private unlocalize(value: any): number {
    return value ? parseInt(value.toString().split('.').join(''), 10) : 0;
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.overlayLoaderService.isLoading = false;
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: this.translate.instant('module.efficiencyAnalysis.efficiencyAnalysisConfigDialog.failedValuesChangeText'),
        descriptionFaClass: 'fa fa-exclamation-triangle fa-2x',
        descriptionFaColor: cssVariables['--ep-error-color'],
        hideClose: true,
      },
      panelClass: ['-md'],
    });
    return throwError(() => error);
  }
}
